<template>
    <div>
        <p>
            Geef per product het aantal op dat je wil retourneren.
        </p>

        <b-table-simple borderless small>
            <b-thead>
                <tr>
                    <b-th style="width:130px;">Aantal retour</b-th>
                    <b-th style="width:80px;">Aantal art.</b-th>
                    <b-th style="width:105px;">Artikel nr.</b-th>
                    <b-th>Omschrijving</b-th>
                    <b-th>Reden</b-th>
                </tr>
            </b-thead>

            <b-tbody>
                <b-tr v-for="(dispatchRequestLine, index) in drqLinesForReturn" :key="index">
                    <b-td>
                        <b-input
                                 v-model.number="dispatchRequestLine.return_qty"
                                 type="number"
                                 :min="0"
                                 :max="dispatchRequestLine.quantity"
                                 style="width:60px;"
                                 size="sm"
                                 class="d-inline-block"
                        />
                        <b-button
                            size="sm"
                            variant="link"
                            class="shadow-none"
                            @click="dispatchRequestLine.return_qty = dispatchRequestLine.quantity"
                        >
                            alles
                        </b-button>
                    </b-td>
                    <b-td>{{ dispatchRequestLine.quantity }}</b-td>
                    <b-td>{{ dispatchRequestLine.product_identifier }}</b-td>
                    <b-td>{{ dispatchRequestLine.product_name }}</b-td>

                    <b-td>
                        <span v-if="dispatchRequestLine.return_qty === 0">
                            N.v.t.
                        </span>
                        <b-form-select  v-else
                                        v-model="dispatchRequestLine.return_reason"
                                        :options="possibleReturnReasons" size="sm"
                                        :disabled="dispatchRequestLine.return_qty === 0"
                        />
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-row>
            <b-col class="text-right mr-1 pb-1">
                <b-button   variant="secondary"
                            size="sm"
                            @click="$emit('close')"
                >
                            {{ $t('omt.gui.general.action.close') }}
                </b-button>
                <b-button   variant="primary"
                            size="sm"
                            class="ml-2"
                            @click="confirmReturns"
                            :disabled="!canSaveChanges"
                >
                    <font-awesome-icon v-if="isSaving" icon="spinner" spin/>
                    Retour bevestigen
                </b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { DispatchRequestsBase } from "../dispatchRequestsBase";

export default {
    name: "ReturnSalesOrderItems",

    extends: DispatchRequestsBase,

    data() {
        return {
            isSaving: false,
            drqLinesForReturn: [],
            returnReasons: [], // Array containing possible return reasons, processed by a computed property, for showing them in the GUI and letting the user pick one.
            isLoading: {
              returnReasons: false,
            },
        };
    },

    props: {
        salesOrder: {
            type: Object,
            required: true,
            default: () => {},
        },

        dispatchRequest: {
            type: Object,
            required: true,
            default: () => {},
        },
    },

    methods: {
        confirmReturns() {
            const salesOrderIdentifier = this.salesOrder.identifier;
            const endpoint = `/api/rest/sales-orders/${salesOrderIdentifier}/dispatch-requests`;

            const lines = this.drqLinesForReturn
                            .filter(line => line.return_qty > 0)
                            .map((line) =>
                                (
                                    {
                                        product_identifier: line.product_identifier,
                                        quantity: line.return_qty,
                                        return_reason: line.return_reason,
                                        product_state: "sellable"
                                    }
                                )
                            );

            if (lines.length === 0) return;

            const configurableDrqInformation = this.$store.state.config["return-announcement"];

            const payload = {
                "identifier": crypto.randomUUID(),
                "dispatch_type": "return",
                "status": "return-announced",
                "lines": lines,
                ...configurableDrqInformation,
            };

            this.isSaving = true;

            this.$http.post(endpoint, payload)
                .then(() => {
                    this.$alert('De retour is succesvol aangemaakt', 'success');
                    this.$emit('dataChanged');
                    this.$emit('close');
                })
                .catch((error) => {
                    this.$alert('Er is iets misgegaan tijdens het aanmaken van de retour', 'error');
                    console.error(`ReturnDrqItems: ${error}`);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        getProductNameBySalesOrderLineId(sales_order_line_id) {
            return this.getSalesOrderLine(sales_order_line_id)?.product_name || 'Onbekend';
        },

        /**
         * Fetches return reasons from the server and updates the component's state.
         * The function makes an HTTP GET request to the '/api/rest/return-reasons' endpoint.
         * On successful retrieval, it updates 'returnReasons' with the response data.
         * In case of an error, it logs the error details including the filename, method name,
         * endpoint used, and a brief description of the error.
         * The 'isLoading["returnReasons"]' flag is used to indicate the loading status
         * before and after the HTTP request.
         */
        fetchReturnReasons() {
            this.isLoading["returnReasons"] = true;

            const endpoint = `/api/rest/return-reasons`;
            this.$http.get(endpoint)
                .then((response) => {
                    this.returnReasons = response.data;
                })
                .catch((error) => {
                    const filename = `ReturnDrqItems.vue`;
                    const method   = `fetchReturnReasons`;
                    const additionalData = { endpoint };
                    const description = `Error while fetching Return Reasons`;
                    console.error(`${filename}: ${method}() - ${description}. Error: ${error}.`, additionalData);
                })
                .finally(() => {
                    this.isLoading["returnReasons"] = false;
                })
        }
    },

    mounted() {
        this.drqLinesForReturn = structuredClone(
            this.dispatchRequest.dispatch_request_lines
            .map(
                (drqLine) => ({
                    return_qty:             0,
                    return_reason:          '',
                    quantity:               drqLine.quantity,
                    product_identifier:     drqLine.product_identifier,
                    product_name:           drqLine.product_name || this.getProductNameBySalesOrderLineId(drqLine.sales_order_line_id),
                })
            )
        );

        this.fetchReturnReasons();
    },

    computed: {
        /**
         * Checks form validity.
         * Only valid if there is at least one item to return,
         * AND all of those items have a valid return reason.
         */
        isFormValid() {
            const result = this.drqLinesForReturn.reduce(
                (acc, item) => {
                    acc.totalReturnQty += item.return_qty;

                    if (item.return_qty > 0 && item.return_reason === '') {
                        acc.isValid = false;
                    }

                    return acc;
                },
                { totalReturnQty: 0, isValid: true }
            );

            return result.totalReturnQty > 0 && result.isValid;
        },

        canSaveChanges() {
            return !this.isSaving && this.isFormValid;
        },


        /**
         * Transforms the list of return reasons to a format suitable for display,
         * based on this.returnReasons.
         *
         * Each reason is mapped to an object with a 'value' and a 'text' property.
         * 'value' is the reason's identifier, and 'text' is a formatted string
         * combining the reason's display name and identifier.
         *
         * @returns {Array.<{value: string, text: string}>} An array of objects with 'value' and 'text' properties.
         */
        possibleReturnReasons() {
            const defaultReturnReason = { value: '', text: 'Kies een reden...', disabled: true };

            const returnReasons = this.returnReasons.map(reason => ({
                value: reason.identifier,
                text: `${reason.display_name} (${reason.identifier})`
            }));

            return [defaultReturnReason, ...returnReasons];
        },
    },
}
</script>

<style scoped>
td {
    line-height: 240%;
}
</style>
